import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Icon, Pane, PaneHead, PhoneScreen,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useMyDay, useTaskActions, type NurseTask } from './api';
import { drName, execTitle, numberWord, shortName } from './format';
import { clearDraft, readDraft, writeDraft } from './drafts';
import { FitInput } from './components/FitInput';
import { NoteField, type NoteFieldHandle } from './components/NoteField';
import { NurseSearch } from './components/NurseSearch';
import { NurseMenu } from './components/NurseMenu';

type Key = 'bpSystolic' | 'bpDiastolic' | 'heartRate' | 'temperature' | 'weight' | 'rbs' | 'spo2' | 'iop' | 'note';

interface Field {
  key: Key | 'bp';
  label: string;
  next: string;
  unit?: string;
  pack?: boolean;
}

const ROW1: Field[] = [
  { key: 'bp', label: 'BP', next: 'HR' },
  { key: 'heartRate', label: 'HR', next: 'Temp' },
  { key: 'temperature', label: 'Temp', next: 'Weight' },
  { key: 'weight', label: 'Weight', next: 'RBS', unit: 'kg' },
];
const ROW2: Field[] = [
  { key: 'rbs', label: 'RBS', next: 'SpO₂', pack: true },
  { key: 'spo2', label: 'SpO₂', next: 'IOP' },
  { key: 'iop', label: 'IOP', next: 'her note', pack: true },
];

/** What each reading may plausibly be; outside it the form names the field. */
const BOUNDS: Partial<Record<Key, { label: string; min: number; max: number }>> = {
  bpSystolic: { label: 'BP top number', min: 40, max: 300 },
  bpDiastolic: { label: 'BP bottom number', min: 20, max: 200 },
  heartRate: { label: 'HR', min: 20, max: 300 },
  temperature: { label: 'Temp', min: 30, max: 45 },
  weight: { label: 'Weight', min: 0.3, max: 500 },
  rbs: { label: 'RBS', min: 10, max: 1500 },
  spo2: { label: 'SpO₂', min: 0, max: 100 },
  iop: { label: 'IOP', min: 0, max: 100 },
};

/** The shell's top-bar primary — "Save — into today’s visit" on this screen. */
function focusSave() {
  document.querySelector<HTMLButtonElement>('.topbar .btn.btn-primary')?.focus();
}

/**
 * Nurse · Vitals — `g_vitals`. THE ROUND, TYPED.
 *
 * Click any field and it types; Tab names the next one, so a whole round goes
 * without lifting a hand. Saving writes a `vital_signs` reading into today's
 * visit (the existing model), with her as the recorder, and the backend judges
 * it against the reviewed bands — a critical value pings the booked doctor.
 *
 * The pane is the round itself: today's vitals tasks, and saving advances to
 * the next one rather than back to a list.
 */
export function NurseVitalsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const day = useMyDay();
  const { saveVitals } = useTaskActions();
  /* What she typed is kept per patient until it is saved (drafts.ts): moving
     through the round, or searching, never throws a reading away. */
  const [values, setValues] = useState<Partial<Record<Key, string>>>(() => readDraft(id));
  const [focus, setFocus] = useState<string | null>(null);
  /** The field the last Save refused, outlined until it is edited. */
  const [bad, setBad] = useState<Key | null>(null);
  const [searching, setSearching] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const noteRef = useRef<NoteFieldHandle>(null);
  /* The shell holds the primary's onClick from its last publish, so Save
     reads the fields through a ref rather than a stale closure. */
  const valuesRef = useRef(values);
  valuesRef.current = values;
  usePageMeta({ title: 'Vitals' });

  const round = useMemo(() => {
    const all = (day.data?.tasks ?? []).filter((t) => t.kind === 'vitals' && !t.handedByMe);
    const now = all.filter((t) => t.id === id);
    const done = all.filter((t) => t.id !== id && t.state === 'done');
    const waiting = all.filter((t) => t.id !== id && (t.state === 'open' || t.state === 'in_progress'));
    return [...now, ...done, ...waiting];
  }, [day.data, id]);
  const task: NurseTask | undefined = round.find((t) => t.id === id);
  const next = round.find((t) => t.id !== id && (t.state === 'open' || t.state === 'in_progress'));
  const closed = task ? task.state === 'done' || task.state === 'cant' : false;

  const hasTask = Boolean(task);
  useEffect(() => {
    setValues(readDraft(id));
  }, [id]);
  useEffect(() => {
    /* Typing starts in BP — the first field of every reading. */
    if (hasTask && !closed) first.current?.focus();
  }, [id, hasTask, closed]);

  const set = (k: Key, v: string) => {
    if (bad === k) setBad(null);
    setValues((cur) => {
      const next = { ...cur, [k]: v };
      writeDraft(id, next);
      return next;
    });
  };
  /* A closed task shows what was stored on it — or says nothing was. */
  const unrecorded = closed && !task?.vitals;
  const shown = (k: Key): string => {
    if (closed) {
      const v = task?.vitals?.[k];
      return v == null ? '' : String(v);
    }
    return values[k] ?? '';
  };

  const save = () => {
    if (!task || closed) return;
    const values = valuesRef.current;
    const num = (k: Key) => {
      const raw = (values[k] ?? '').trim();
      if (!raw) return undefined;
      const n = Number(raw.replace(',', '.'));
      return Number.isFinite(n) ? n : NaN;
    };
    const payload: Record<string, number | string | undefined> = {
      bpSystolic: num('bpSystolic'), bpDiastolic: num('bpDiastolic'), heartRate: num('heartRate'),
      temperature: num('temperature'), weight: num('weight'), rbs: num('rbs'), spo2: num('spo2'), iop: num('iop'),
      note: values.note?.trim() || undefined,
    };
    for (const [k, b] of Object.entries(BOUNDS) as [Key, { label: string; min: number; max: number }][]) {
      const v = payload[k];
      if (typeof v !== 'number') continue;
      if (Number.isNaN(v)) {
        setBad(k);
        toast.error(`${b.label} is not a number`);
        return;
      }
      if (v < b.min || v > b.max) {
        setBad(k);
        toast.error(`${b.label} ${v} is outside ${b.min}–${b.max}`);
        return;
      }
    }
    setBad(null);
    if (Object.values(payload).every((v) => v === undefined)) {
      toast.error('Nothing typed yet — click a field and type');
      return;
    }
    saveVitals.mutate(
      { id: task.id, values: payload },
      {
        onSuccess: (res) => {
          clearDraft(task.id);
          const doc = drName(task.requestedBy) || 'the doctor';
          if (res.critical) toast.error(`A critical value — saved, and ${doc} was pinged now`);
          else toast.success('Saved into today’s visit');
          navigate(next ? `/nurse/day/vitals/${next.id}` : '/nurse/day');
        },
        onError: (e) => toast.error((e as Error).message || 'The reading did not save'),
      },
    );
  };

  const number = task?.appointment ? `#${task.appointment.number}` : '';

  const card = (f: Field, second = false, phone = false) => {
    const focused = focus === f.key;
    const style: CSSProperties = {
      flex: 1, margin: 0, cursor: closed ? 'default' : 'text',
      ...(second ? { borderColor: 'var(--brand-300)' } : {}),
      ...(focused ? { borderColor: 'var(--brand-500)', background: 'var(--brand-100)' } : {}),
      ...(bad && (f.key === bad || (f.key === 'bp' && (bad === 'bpSystolic' || bad === 'bpDiastolic')))
        ? { borderColor: 'var(--danger-600)', background: 'var(--danger-100)' } : {}),
    };
    const value = f.key === 'bp' ? `${shown('bpSystolic')}${shown('bpDiastolic')}` : shown(f.key);
    const blank = unrecorded ? 'not recorded' : '—';
    return (
      <label key={f.key} className="card" style={style}
        onFocusCapture={() => setFocus(f.key)} onBlurCapture={() => setFocus(null)}>
        <p className="t-ovl mut">{f.label}</p>
        <p className="t-heading mt4" style={focused ? { color: 'var(--brand-600)' } : undefined}>
          {f.key === 'bp' ? (
            unrecorded ? blank : (
              <>
                <FitInput ref={first} aria-label="BP systolic" inputMode="numeric" disabled={closed} maxLength={3}
                  value={shown('bpSystolic')} onChange={(e) => set('bpSystolic', e.target.value)} placeholder="—" />
                {' / '}
                <FitInput aria-label="BP diastolic" inputMode="numeric" disabled={closed} maxLength={3}
                  value={shown('bpDiastolic')} onChange={(e) => set('bpDiastolic', e.target.value)} placeholder="—" />
              </>
            )
          ) : unrecorded ? blank : (
            <>
              <FitInput aria-label={f.label} inputMode="decimal" disabled={closed} maxLength={6}
                value={shown(f.key)} onChange={(e) => set(f.key as Key, e.target.value)} placeholder="—" />
              {f.unit && value ? ` ${f.unit}` : ''}
            </>
          )}
          {closed ? null
            : focused ? <span style={{ opacity: 0.6 }}>▌</span>
            : <> <Icon name="pencil" size="sm" className="mut" /></>}
        </p>
        {phone ? null : <p className="t-cap mut mt8">{closed ? '\u00a0' : focused ? `typing · Tab → ${f.next}` : `Tab → ${f.next}`}</p>}
      </label>
    );
  };

  useShellChrome(
    {
      raw: true,
      title: task ? execTitle(task) : 'Vitals',
      sub: (
        <>
          <button type="button" className="lnk" onClick={() => navigate('/nurse/day')}>‹ My day</button>
          {' '}▸ Vitals{number ? ` · ${number}` : ''}
        </>
      ),
      /* `g_n4` — the mini-form. The desk lays the fields in two flex rows
         because it has the width; a 390px column stacks them, and they are the
         SAME cards, so a unit or a normal range cannot differ between them. */
      phone: (
        <PhoneScreen
          className="nurse-phone"
          crumb="My day"
          title={task ? execTitle(task) : 'Vitals'}
          onBack={() => navigate('/nurse/day')}
          menu={(close) => <NurseMenu door="day" onClose={close} />}
        >
          {/* Three rows of cards (BP·HR, Temp·Weight, RBS·SpO₂·IOP): the cards
              are flex children, so they need a flex row to share its width. */}
          {[ROW1.slice(0, 2), ROW1.slice(2), ROW2].map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {row.map((f) => card(f, i === 2, true))}
            </div>
          ))}
          <p className="t-cap mut mb8">
            the extra fields come from the specialty pack — ophtho adds IOP, chest adds peak flow
          </p>
          {closed ? (
            <div className="card">
              <p className="t-sec sec w6">
                {unrecorded ? 'Closed without a reading on this task — nothing was recorded here.' : 'Taken and saved into today’s visit.'}
              </p>
            </div>
          ) : (
            <div className="card" style={{ borderColor: 'var(--danger-600)', background: 'var(--danger-100)' }}>
              <p className="t-sec w6 red">
                If a value is critical — BP 180/120, SpO₂ 88 — it saves, flags RED on the doctor’s queue row, and pings them now
              </p>
            </div>
          )}
          <div className="card" style={{ borderStyle: 'dashed' }}>
            <p className="t-ovl mut">Her note <span className="t-cap">(optional)</span></p>
            <p className="t-body mt4">
              {closed && !shown('note') ? (unrecorded ? 'not recorded' : '—') : (
                <NoteField label="Her note" value={shown('note')} onChange={(v) => set('note', v)}
                  placeholder="add a note" disabled={closed} pencil={!closed} />
              )}
            </p>
          </div>
          {closed ? null : (
            <>
              <button type="button" className="btn btn-primary mt12" onClick={save}>Save — into today’s visit</button>
              <p className="t-cap mut mt12" style={{ textAlign: 'center' }}>
                queue row flips to vitals ✓ · doctor sees them instantly
              </p>
            </>
          )}
        </PhoneScreen>
      ),
      search: 'Search my day…',
      onSearch: () => setSearching(true),
      primary: !task || closed ? undefined : { label: 'Save — into today’s visit', onClick: save },
      pane: (
        <Pane
          head={
            <PaneHead
              overline="The round · the screen’s own second half"
              title="Today’s round"
              sub={`${numberWord(round.length)} patient${round.length === 1 ? '' : 's'} · saving advances to the next`}
            />
          }
          foot={next ? (
            <button type="button" className="btn btn-outline grow" style={{ minHeight: 44 }}
              onClick={() => navigate(`/nurse/day/vitals/${next.id}`)}>
              Skip to {shortName(next.patient)}{next.appointment ? ` #${next.appointment.number}` : ''}
            </button>
          ) : undefined}
        >
          {round.length ? round.map((t) => {
            const isNow = t.id === id;
            const done = t.state === 'done';
            return (
              <div key={t.id} className="card" role={isNow || done ? undefined : 'button'} tabIndex={isNow || done ? undefined : 0}
                onClick={isNow || done ? undefined : () => navigate(`/nurse/day/vitals/${t.id}`)}
                onKeyDown={(e) => { if (!isNow && !done && e.key === 'Enter') navigate(`/nurse/day/vitals/${t.id}`); }}
                style={isNow || done ? undefined : { cursor: 'pointer' }}>
                <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                  <p className="t-strong grow">
                    {shortName(t.patient)}{' '}
                    {t.appointment ? <span className="t-cap mut">#{t.appointment.number}</span> : null}
                  </p>
                  {isNow && !done ? <span className="chip chip-warn chip-sm">now</span>
                    : done ? <span className="chip chip-success chip-sm">done ✓</span>
                    : <span className="chip chip-outline chip-sm">waiting</span>}
                </div>
              </div>
            );
          }) : (
            <div className="emptystate"><p className="t-sec">No vitals are due today — a prep task lands here when a patient arrives.</p></div>
          )}
          <p className="t-cap mut center mt12">save moves to the next — without going back to a list</p>
        </Pane>
      ),
    },
    [id, task?.state, round.map((t) => `${t.id}:${t.state}`).join('|'), next?.id, closed, values, focus, unrecorded],
  );

  if (!day.data) {
    return (
      <div className="maie desk nurse-root"><div className="dcontent">
        <p className="t-sec mut">{day.isError ? 'The round could not be loaded — it retries on its own.' : 'Loading the round…'}</p>
      </div></div>
    );
  }
  if (!task) {
    return (
      <div className="maie desk nurse-root"><div className="dcontent">
        <div className="emptystate">
          <p className="es-t">This vitals task is not on your day.</p>
          <p className="t-sec mut">It may have been handed over or closed by a colleague.</p>
          <p className="mt12">
            <button type="button" className="dchip dchip-outline" onClick={() => navigate('/nurse/day')}>Back to My day</button>
          </p>
        </div>
      </div></div>
    );
  }

  const noteFocused = focus === 'note';
  return (
    <div className="maie desk nurse-root">
      <div className="dcontent">
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>{ROW1.map((f) => card(f))}</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          {ROW2.map((f) => card(f, true))}
          {/* The note is running text: it wraps inside its card (flex 2 of the
              row's 1/1/1/2) and Tab from it lands on Save, which the shell
              draws in the top bar — ahead of the fields in the DOM. */}
          <div className="card" onFocusCapture={() => setFocus('note')} onBlurCapture={() => setFocus(null)}
            onClick={(e) => {
              /* A click ON the text places the caret itself; anywhere else in
                 the card puts it after the last word. */
              if (!closed && !(e.target as HTMLElement).closest('.nurse-note')) noteRef.current?.focus();
            }}
            style={{ flex: 2, minWidth: 0, margin: 0, borderStyle: 'dashed', cursor: closed ? 'default' : 'text',
              ...(noteFocused ? { borderColor: 'var(--brand-500)', background: 'var(--brand-100)' } : {}) }}>
            <p className="t-ovl mut">Her note</p>
            <p className="t-heading mt4" style={noteFocused ? { color: 'var(--brand-600)' } : undefined}>
              {closed && !shown('note') ? (unrecorded ? 'not recorded' : '—') : (
                <NoteField ref={noteRef} label="Her note" value={shown('note')} onChange={(v) => set('note', v)}
                  placeholder="—" mutedPlaceholder={false} disabled={closed} onTab={focusSave}
                  pencil={!noteFocused && !closed} />
              )}
            </p>
            <p className="t-cap mut mt8">{closed ? '\u00a0' : noteFocused ? 'typing · Tab → Save' : 'Tab → Save'}</p>
          </div>
        </div>

        {closed ? (
          unrecorded ? (
            <div className="card">
              <p className="t-sec sec w6">Closed without a reading on this task — nothing was recorded here.</p>
            </div>
          ) : (
            <div className="card" style={{ borderColor: 'var(--success-600)' }}>
              <p className="t-sec grn w6">Taken and saved into today’s visit — the chart judges it against the reviewed bands.</p>
            </div>
          )
        ) : (
          <div className="card" style={{ borderColor: 'var(--danger-600)', background: 'var(--danger-100)' }}>
            <p className="t-sec w6 red">
              A critical value — BP 180/120, SpO₂ 88 — saves and pings the doctor now
            </p>
          </div>
        )}
        <p className="t-sec mut mt12">
          Extra fields come from the specialty pack · Tab moves field to field — a whole round without lifting a hand.
        </p>
      </div>
      <NurseSearch open={searching} onClose={() => setSearching(false)} />
    </div>
  );
}
